const MAX_GROUPS = 12;
const FRAMES_PER_SECOND = 60;
const DEFAULT_SPEED = 0.75;
const VISIBILITY_MARGIN = "200px 0px";

/**
 * Infinite marquee driven by a CSS animation.
 *
 * JS is only responsible for cloning the item group, exposing CSS variables
 * (--marquee-shift, --marquee-duration) and pausing the animation off-screen.
 *
 * Expected markup:
 * .marquee[data-marquee] > .marquee-inner > .marquee-group > .marquee-item
 */
export default class Marquee {
    /**
     * @param {HTMLElement|string} element - Marquee container or CSS selector.
     * @param {Object} [options] - Configuration options.
     * @param {number} [options.speed] - Speed in pixels per frame (at 60fps).
     * @param {"left"|"right"} [options.direction] - Scroll direction.
     * @param {boolean} [options.pauseOnHover] - Pause animation on hover.
     */
    constructor(element, options = {}) {
        /** @type {HTMLElement|null} */
        this.container = typeof element === "string" ? document.querySelector(element) : element;

        if (!this.container || this.container.marqueeInstance) return;
        this.container.marqueeInstance = this;

        const config = this.resolveOptions(options);

        /** @type {number} */
        this.speed = config.speed;
        /** @type {"left"|"right"} */
        this.direction = config.direction;
        /** @type {boolean} */
        this.pauseOnHover = config.pauseOnHover;

        /** @type {HTMLElement|null} */
        this.track = null;
        /** @type {HTMLElement|null} */
        this.group = null;
        /** @type {HTMLElement[]} */
        this.clones = [];
        /** @type {number|null} */
        this.updateFrame = null;
        /** @type {ResizeObserver|null} */
        this.resizeObserver = null;
        /** @type {IntersectionObserver|null} */
        this.visibilityObserver = null;

        this.init();
    }

    /**
     * Merges options by priority: JS options > data-marquee JSON > data-* attributes > defaults.
	 * 
     * @param {Object} options - Options passed to the constructor.
     * @returns {{speed: number, direction: string, pauseOnHover: boolean}}
     */
    resolveOptions(options) {
        const attr = this.parseDataAttribute();
        const dataset = this.container.dataset;

        const speed = Number(options.speed ?? attr.speed ?? dataset.marqueeSpeed);
        const direction = options.direction || attr.direction || dataset.marqueeDirection || "left";

        const datasetPause =
            dataset.marqueePauseOnHover !== undefined ? dataset.marqueePauseOnHover !== "false" : undefined;
        const pauseOnHover = Boolean(options.pauseOnHover ?? attr.pauseOnHover ?? datasetPause ?? false);

        return {
            speed: speed > 0 ? speed : DEFAULT_SPEED,
            direction,
            pauseOnHover,
        };
    }

    /**
     * Parses the JSON stored in the data-marquee attribute.
	 * 
     * @returns {Object} Parsed options or an empty object.
     */
    parseDataAttribute() {
        const raw = this.container.getAttribute("data-marquee");

        if (!raw || !raw.trim().startsWith("{")) return {};

        try {
            return JSON.parse(raw);
        } catch (error) {
            console.warn("Marquee: invalid JSON in data-marquee attribute", error);
            return {};
        }
    }

    /**
     * Prepares the DOM and starts all observers.
     */
    init() {
        this.track = this.container.querySelector(".marquee-inner");
        if (!this.track) return;

        this.group = this.ensureGroup();

        this.applyModifierClasses();
        this.observeSize();
        this.observeVisibility();
        this.scheduleUpdate();
    }

    /**
     * Returns the item group, wrapping the track content if the group is missing.
     * @returns {HTMLElement}
     */
    ensureGroup() {
        const existing = this.track.querySelector(":scope > .marquee-group");
        if (existing) return existing;

        const group = document.createElement("div");
        group.className = "marquee-group";

        while (this.track.firstChild) {
            group.appendChild(this.track.firstChild);
        }

        this.track.appendChild(group);
        return group;
    }

    /**
     * Toggles modifier classes used by the stylesheet.
     */
    applyModifierClasses() {
        this.container.classList.toggle("marquee--reverse", this.direction === "right");
        this.container.classList.toggle("marquee--pause-hover", this.pauseOnHover);
    }

    /**
     * Recalculates the layout whenever the container or the group changes size
     * (window resize, late-loaded images, web fonts).
     */
    observeSize() {
        this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
        this.resizeObserver.observe(this.container);
        this.resizeObserver.observe(this.group);
    }

    /**
     * Toggles the "is-inview" class so the animation runs only near the viewport.
     */
    observeVisibility() {
        this.visibilityObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    this.container.classList.toggle("is-inview", entry.isIntersecting);
                });
            },
            { rootMargin: VISIBILITY_MARGIN, threshold: 0 },
        );

        this.visibilityObserver.observe(this.container);
    }

    /**
     * Collapses multiple update requests into a single one per frame.
     */
    scheduleUpdate() {
        if (this.updateFrame) return;

        this.updateFrame = requestAnimationFrame(() => {
            this.updateFrame = null;
            this.update();
        });
    }

    /**
     * Measures the layout, syncs clones and publishes CSS variables.
     */
    update() {
        const { groupWidth, containerWidth } = this.measure();

        // Zero width means hidden or not loaded yet; ResizeObserver will call us again.
        if (groupWidth <= 0 || containerWidth <= 0) return;

        this.syncClones(this.getCloneCount(groupWidth, containerWidth));
        this.applyCssVariables(groupWidth);
        this.container.classList.add("is-ready");
    }

    /**
     * Reads the current widths in one place to avoid layout thrashing.
	 * 
     * @returns {{groupWidth: number, containerWidth: number}}
     */
    measure() {
        return {
            groupWidth: this.group.getBoundingClientRect().width,
            containerWidth: this.container.clientWidth,
        };
    }

    /**
     * Calculates how many clones are needed to cover the container
     * plus one extra group for the shift distance.
	 * 
     * @param {number} groupWidth - Width of a single group in pixels.
     * @param {number} containerWidth - Width of the container in pixels.
     * @returns {number}
     */
    getCloneCount(groupWidth, containerWidth) {
        const totalGroups = Math.min(MAX_GROUPS, Math.ceil(containerWidth / groupWidth) + 1);
        return totalGroups - 1;
    }

    /**
     * Publishes the shift distance and the animation duration to CSS.
	 * 
     * @param {number} groupWidth - Width of a single group in pixels.
     */
    applyCssVariables(groupWidth) {
        const duration = groupWidth / (this.speed * FRAMES_PER_SECOND);

        this.container.style.setProperty("--marquee-shift", `${groupWidth}px`);
        this.container.style.setProperty("--marquee-duration", `${duration}s`);
    }

    /**
     * Adds or removes clones until their number matches the target.
	 * 
     * @param {number} count - Desired number of clones.
     */
    syncClones(count) {
        this.removeClones(count);
        this.addClones(count);
    }

    /**
     * Removes extra clones.
	 * 
     * @param {number} count - Number of clones to keep.
     */
    removeClones(count) {
        while (this.clones.length > count) {
            this.clones.pop().remove();
        }
    }

    /**
     * Appends missing clones using a single DOM insertion.
	 * 
     * @param {number} count - Desired number of clones.
     */
    addClones(count) {
        if (this.clones.length >= count) return;

        const fragment = document.createDocumentFragment();

        while (this.clones.length < count) {
            const clone = this.createClone();
            this.clones.push(clone);
            fragment.appendChild(clone);
        }

        this.track.appendChild(fragment);
    }

    /**
     * Creates an accessibility-safe copy of the group.
	 * 
     * @returns {HTMLElement}
     */
    createClone() {
        const clone = this.group.cloneNode(true);

        clone.setAttribute("aria-hidden", "true");
        clone.inert = true;

        clone.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
        clone.querySelectorAll("img").forEach((img) => {
            img.loading = "eager";
            img.decoding = "async";
        });

        return clone;
    }

    /**
     * Removes observers, clones, classes and instance reference.
     */
    destroy() {
        if (this.updateFrame) {
            cancelAnimationFrame(this.updateFrame);
            this.updateFrame = null;
        }

        this.resizeObserver?.disconnect();
        this.visibilityObserver?.disconnect();
        this.removeClones(0);

        if (this.container) {
            this.container.classList.remove("is-ready", "is-inview", "marquee--reverse", "marquee--pause-hover");
            delete this.container.marqueeInstance;
        }
    }

    /**
     * Initializes every marquee found on the page.
	 * 
     * @param {string} [selector="[data-marquee]"] - CSS selector for marquee elements.
     * @returns {Marquee[]} Created instances.
     */
    static initAll(selector = "[data-marquee]") {
        return Array.from(document.querySelectorAll(selector)).map((el) => new Marquee(el));
    }
}
